import React, { useEffect, useMemo, useState } from 'react';
import {
  Layout,
  Card,
  Typography,
  Input,
  Button,
  Table,
  Tag,
  Space,
  message,
  Spin,
  Divider,
  Modal,
  Flex,
} from "antd";
import {
  KeyOutlined,
  PlusOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
   BarChartOutlined,
} from '@ant-design/icons';
import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import ApiEndpointsList from './Apiendpointslist';
import './API.css';

const { Content } = Layout;
const { Title, Text } = Typography;

const API_BASE_URL ='https://api.exellius.com/api/v1/api-keys';

const getAuthHeaders = (withJson = true) => {
  const headers = new Headers();
  const token = sessionStorage.getItem('access_token');

  if (withJson) {
    headers.append('Content-Type', 'application/json');
  }

  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  return headers;
};

const parseResponse = async (response) => {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (error) {
    return { raw: text };
  }
};

export default function API() {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [name, setName] = useState('v1');
  const [collapsed, setCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchApiKeys = async () => {
    setLoading(true);

    try {
      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: getAuthHeaders(false),
        redirect: 'follow',
      });

      const result = await parseResponse(response);

      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch API keys');
      }

      const keys = result.apiKeys || result.data || [];
      setApiKeys(Array.isArray(keys) ? keys : []);
    } catch (error) {
      message.error(error.message || 'Unable to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleCreateKey = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      message.warning('Please enter an API key name');
      return;
    }

    setCreating(true);

    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ name: trimmedName }),
        redirect: 'follow',
      });

      const result = await parseResponse(response);

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create API key');
      }

      setIsModalOpen(false);
      message.success('API key created successfully');
      setName('v1');
      await fetchApiKeys();
    } catch (error) {
      message.error(error.message || 'Unable to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      message.success('API token copied');
    } catch (error) {
      message.error('Unable to copy token');
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (text) => <Text strong>{text || 'Untitled'}</Text>,
      },
      {
        title: 'Token',
        dataIndex: 'token',
        key: 'token',
        width: 320,
        render: (token) => (
          <Space size="small">
            <Text code style={{ maxWidth: 260, display: 'inline-block' }} ellipsis>
              {token || '—'}
            </Text>
            {token && (
              <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(token)} />
            )}
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'is_active',
        key: 'is_active',
        render: (isActive) => (
          <Tag color={isActive ? 'success' : 'default'} icon={isActive ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
            {isActive ? 'Active' : 'Revoked'}
          </Tag>
        ),
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (value) => <Text>{value ? new Date(value).toLocaleString() : '—'}</Text>,
      },
      {
        title: 'Last Used',
        dataIndex: 'last_used_at',
        key: 'last_used_at',
        render: (value) => <Text>{value ? new Date(value).toLocaleString() : 'Never'}</Text>,
      },
    ],
    []
  );

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fb' }}>
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />

      <Layout>
        <AppHeader />

        <Content
          className="api-page"
          style={{
            padding: 24,
            height: '100vh',
            overflow: 'auto',
            background: '#f5f7fb',
          }}
        >
          <Card className="api-page-card" bordered={false}>
            <Flex
  justify="space-between"
  align="flex-start"
  style={{
    width: "100%",
    marginBottom: 20,
  }}
>
  {/* Left Side */}
  <div>
    <Title
      level={4}
      style={{
        margin: 0,
        marginBottom: 4,
      }}
    >
      API Key Management
    </Title>

    <Text type="secondary">
      Create and manage API keys for integrations and automation workflows.
    </Text>
  </div>

  {/* Right Side */}
  <Flex gap={8}>
    <Button
      type="primary"
      icon={<BarChartOutlined />}
      onClick={() => {
        window.location.href = "/apiv1/analytics";
      }}
    >
      API Analytics
    </Button>

    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={() => setIsModalOpen(true)}
    >
      Create API Key
    </Button>
  </Flex>
</Flex>

            
            <div className="api-list-section" style={{ marginTop: 24 }}>
              

              {loading ? (
                <div className="api-loader">
                  <Spin size="large" />
                </div>
              ) : (
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={apiKeys}
                  pagination={false}
                  locale={{ emptyText: 'No API keys yet' }}
                />
              )}
            </div>
          </Card>

          <ApiEndpointsList />
        </Content>
        <Modal
  title="Create API Key"
  open={isModalOpen}
  onCancel={() => {
    setIsModalOpen(false);
    setName("v1");
  }}
  footer={[
    <Button
      key="cancel"
      onClick={() => {
        setIsModalOpen(false);
        setName("v1");
      }}
    >
      Cancel
    </Button>,
    <Button
      key="create"
      type="primary"
      loading={creating}
      icon={<PlusOutlined />}
      onClick={handleCreateKey}
    >
      Create API Key
    </Button>,
  ]}
>
  <Input
    size="large"
    value={name}
    onChange={(e) => setName(e.target.value)}
    placeholder="Enter API key name"
    prefix={<KeyOutlined />}
    onPressEnter={handleCreateKey}
  />
</Modal>
      </Layout>
    </Layout>
  );
}
